import {
  Button,
  Dialog,
  DialogClose,
  Switch,
  Typography,
} from "@hellocarbo/lasagne";
import CopyToClipboardButton from "@ui/CopyToClipboardButton";
import { Settings } from "lucide-react";
import { use, useState } from "react";
import { useTranslation } from "react-i18next";
import { ActionFunctionArgs, data, useFetcher } from "react-router";
import invariant from "tiny-invariant";

import FlyingCat from "@/assets/common/other_icons/flying-cat.svg?react";
import { UserContext } from "@/contexts";
import { PortalContainerContext } from "@/contexts";
import { TReviewSharingStatus } from "@/types";

import BaseCard, { ActionButton } from "./BaseCard";

export async function action({ request, params }: ActionFunctionArgs) {
  const { reviewId } = params;
  invariant(reviewId, "No review id found!");

  const formData = await request.formData();

  const response = await fetch(
    `/api/client/reviews/${reviewId}/sharing_settings`,
    {
      method: "PATCH",
      body: JSON.stringify({
        sharing_settings: {
          hide_comparison: formData.get("comparison"),
          hide_economic_intensity: formData.get("economicIntensity"),
          hide_spending: formData.get("marketingSpending"),
        },
      }),
      headers: { "Content-Type": "application/json" },
    }
  );

  if (response.status !== 200) {
    throw data("Failed to update the review sharing settings", {
      status: response.status,
    });
  }

  return { ok: true };
}

type SharingStatusProps = {
  status: TReviewSharingStatus;
  hideComparison?: boolean;
  hideEconomicIntensity?: boolean;
  hideMarketingSpending?: boolean;
  url?: string;
  updateSharingStatus: (status: TReviewSharingStatus) => void;
};

function SharingSettings({
  hideComparison,
  hideEconomicIntensity,
  hideMarketingSpending,
  status,
  url,
  updateSharingStatus,
}: SharingStatusProps) {
  const { t } = useTranslation(["communicate", "formatter"]);
  const { container } = use(PortalContainerContext);
  const fetcher = useFetcher();

  const [isPublic, setIsPublic] = useState(
    status === TReviewSharingStatus.public
  );

  const [isComparisonChecked, setIsComparisonChecked] =
    useState(!hideComparison);
  const [isEconomicIntensityChecked, setIsEconomicIntensityChecked] = useState(
    !hideEconomicIntensity
  );
  const [isMarketingSpendingChecked, setIsMarketingSpendingChecked] = useState(
    !hideMarketingSpending
  );

  const handleClick = () => {
    fetcher.submit(
      {
        comparison: !isComparisonChecked,
        economicIntensity: !isEconomicIntensityChecked,
        marketingSpending: !isMarketingSpendingChecked,
      },
      {
        action: "sharing_settings",
        method: "patch",
      }
    );
  };

  const handleSharingStatusChange = (isPublic: boolean) => {
    setIsPublic(isPublic);
    updateSharingStatus(
      isPublic ? TReviewSharingStatus.public : TReviewSharingStatus.private
    );
  };

  const { user } = use(UserContext);
  const isFreeTrial = user?.company.subscriptionType === "start";

  return (
    <BaseCard
      Icon={FlyingCat}
      title={t("communicate:sharingSettings.title")}
      text={t("communicate:sharingSettings.text")}
      headerContent={
        <div className="gap-x-2xs flex items-center">
          <Typography as="span">
            {isPublic
              ? t("communicate:sharingSettings.enabled")
              : t("communicate:sharingSettings.disabled")}
          </Typography>
          <Switch
            onCheckedChange={handleSharingStatusChange}
            defaultChecked={isPublic}
          />
        </div>
      }
    >
      <div className="mt-lg gap-y-lg flex flex-col">
        {isPublic && url && (
          <CopyToClipboardButton
            url={url}
            toastProps={{
              title: t("communicate:sharingSettings.linkCopied"),
              toastType: "simple",
              intent: "positive",
            }}
          />
        )}
        <div className="mt-3xs w-[265px]">
          {isFreeTrial ? (
            <ActionButton>
              {t("communicate:sharingSettings.freeTrialCta")}
            </ActionButton>
          ) : isPublic ? (
            <Dialog
              title={t("communicate:sharingSettings.modal.title")}
              description={t("communicate:sharingSettings.modal.description")}
              portalContainer={container as Element}
              trigger={
                <Button PrefixIcon={Settings} shape="soft" intent="secondary">
                  {t("communicate:sharingSettings.modal.trigger")}
                </Button>
              }
              footer={
                <>
                  <DialogClose asChild>
                    <Button shape="link">
                      {t("communicate:sharingSettings.modal.cancel")}
                    </Button>
                  </DialogClose>
                  <DialogClose asChild>
                    <Button shape="solid" onClick={handleClick}>
                      {t("communicate:sharingSettings.modal.validate")}
                    </Button>
                  </DialogClose>
                </>
              }
            >
              <div className="gap-y-xs flex flex-col">
                <div className="gap-xs grid grid-cols-[1fr_auto_auto] items-center">
                  <Typography weight="semibold">
                    {t("communicate:sharingSettings.modal.comparison")}
                  </Typography>
                  <Typography className="text-grayscale">
                    {isComparisonChecked
                      ? t("communicate:sharingSettings.modal.shown")
                      : t("communicate:sharingSettings.modal.hidden")}
                  </Typography>
                  <Switch
                    onCheckedChange={setIsComparisonChecked}
                    defaultChecked={isComparisonChecked}
                  />
                </div>
                <div className="gap-xs grid grid-cols-[1fr_auto_auto] items-center">
                  <Typography weight="semibold">
                    {t("communicate:sharingSettings.modal.economicIntensity")}
                  </Typography>
                  <Typography className="text-grayscale">
                    {isEconomicIntensityChecked
                      ? t("communicate:sharingSettings.modal.shown")
                      : t("communicate:sharingSettings.modal.hidden")}
                  </Typography>
                  <Switch
                    onCheckedChange={setIsEconomicIntensityChecked}
                    defaultChecked={isEconomicIntensityChecked}
                  />
                </div>
                <div className="gap-xs grid grid-cols-[1fr_auto_auto] items-center">
                  <Typography weight="semibold">
                    {t("communicate:sharingSettings.modal.marketingSpending")}
                  </Typography>
                  <Typography className="text-grayscale">
                    {isMarketingSpendingChecked
                      ? t("communicate:sharingSettings.modal.shown")
                      : t("communicate:sharingSettings.modal.hidden")}
                  </Typography>
                  <Switch
                    onCheckedChange={setIsMarketingSpendingChecked}
                    defaultChecked={isMarketingSpendingChecked}
                  />
                </div>
              </div>
            </Dialog>
          ) : null}
        </div>
      </div>
    </BaseCard>
  );
}

export default SharingSettings;
